// App-side delegate for `@heidi/commerce/data`.
//
// The request moved; every effect around it stayed here, which is the whole
// point of the split — this file is nothing but app reactions and the
// app-resolved values the request needs. Three of them are worth naming:
//
//   - The Apple IAP guard CALL is here, not in the package. CLAUDE.md requires
//     the suppression to sit in the same file as the billing path it
//     suppresses so it is visible in every diff; only the boolean crosses.
//   - `getRewardfulReferral()` and the two Amplitude ids cross as RESOLVERS
//     rather than values, so the package still reads them at click time. The
//     Rewardful script loads asynchronously and an Amplitude session rolls
//     over on inactivity, so a render-time read could send an empty
//     `client_reference_id` or a dead session id.
//   - `linkAccounts` is injected from here because its route
//     (`users/linked-account`) is a user-identity resource, not a billing one.
import { billingMessages } from '@/hooks/mutation/billing/billing.messages';
import { useCheckoutRequestContext } from '@/hooks/mutation/billing/useCheckoutRequestContext';
import {
  trackPaywallCheckout,
  trackReferralCouponAppliedToCheckout,
} from '@/lib/analytics/commerce/checkout-events';
import { linkAccounts } from '@/pages/api/scribeOpenAPI';
import { isTauri, openUrlInTauri, tauriStripeRedirectUrl } from '@/tauri/utils';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import * as amplitude from '@amplitude/analytics-browser';
import {
  clearPostCheckoutSuccessRedirectCookie,
  setPostCheckoutSuccessRedirectCookie,
  useBillingErrorToast,
} from '@heidi/commerce';
import {
  getRewardfulReferral,
  useMutateCheckoutV2 as useMutateCheckoutV2Core,
} from '@heidi/commerce/data';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

export function useMutateCheckoutV2() {
  const intl = useIntl();
  const showBillingError = useBillingErrorToast();
  const router = useRouter();

  const { useOpenApiCheckout, ccRequired } = useCheckoutRequestContext();

  return useMutateCheckoutV2Core({
    useOpenApiCheckout,
    ccRequired,
    checkoutSuppressed: isAppleCompliantBuild(),
    resolveReferral: () => getRewardfulReferral(),
    resolveAttribution: () => ({
      amplitudeDeviceId: amplitude.getDeviceId() ?? null,
      amplitudeSessionId: amplitude.getSessionId() ?? null,
    }),
    resolveFeEndpoint: (feEndpoint) =>
      isTauri() ? tauriStripeRedirectUrl() : feEndpoint,
    linkAccounts: (kindeUserId) => linkAccounts({ kindeUserId }),
    onBeforeCheckout: (input) => {
      if (!isTauri() && input.postCheckoutSuccessRedirectUrl) {
        setPostCheckoutSuccessRedirectCookie(input.postCheckoutSuccessRedirectUrl);
      }
    },
    onReferralCouponApplied: ({ referralId, couponId }) => {
      trackReferralCouponAppliedToCheckout({
        referralId,
        couponId,
      });
    },
    onCheckoutUrl: (checkoutSessionUrl) => {
      if (isTauri()) {
        void openUrlInTauri(checkoutSessionUrl);
      } else {
        void router.push(checkoutSessionUrl);
      }
    },
    onCheckoutFailed: () => {
      showBillingError({
        title: intl.formatMessage(billingMessages.couldntStartCheckout),
      });
    },
    onCheckoutStarted: (planPeriod) => {
      trackPaywallCheckout(planPeriod);
    },
    onCheckoutError: (input) => {
      if (!isTauri() && input.postCheckoutSuccessRedirectUrl) {
        clearPostCheckoutSuccessRedirectCookie();
      }
    },
  });
}
